import uniqueId from 'lodash/uniqueId'
import { StrictMode, useRef } from 'react'
import type { VFC } from 'react'
import { StoryfirstUnit, storyfirstUnits } from '@opallabs/concepts'
import { Conditional } from '@opallabs/kuiper'
import { formatCurrencyAmount, isPositiveNumber } from '@opallabs/lambda'
import { BudgetAllocation } from '../BudgetAllocation'
import { BudgetHeader } from '../BudgetHeader'
import * as styles from './styles'
import type { BudgetPanelProps } from './types'

/**
 * ‘Rollup’ view of a `context` budget. If `context` is a story, displays the
 * total amount (`max`) budgeted for the story and its moments (`momentValue`)
 * and content (`contentValue`). If `context` is a moment, displays the total
 * amount budgeted for the moment (`max`) and its content (`contentValue`).
 *
 * Note that `momentValue` and/or `contentValue` may be larger than `max`; this
 * excess will be reflected in the UI.
 */
export const BudgetPanel: VFC<BudgetPanelProps> = ({
  contentValue = 0,
  context,
  currency = 'USD',
  max,
  momentValue = 0,
  styleOverrides = {},
  theme = 'light',
}) => {
  const contentDescriptionId = useRef(uniqueId('photon-budget-content'))
  const momentDescriptionId = useRef(uniqueId('photon-budget-moment'))
  const describedBy = [contentDescriptionId.current]
  if (context === StoryfirstUnit.Story)
    describedBy.unshift(momentDescriptionId.current)

  const headerTitle = storyfirstUnits`${context} budget`

  const contentMax =
    context === StoryfirstUnit.Story && isPositiveNumber(momentValue)
      ? momentValue
      : max

  const groupLabel = [
    'Total ',
    headerTitle,
    ': ',
    formatCurrencyAmount({
      currency,
      value: max,
    }),
  ]

  const remaining = max - Math.max(momentValue, contentValue)
  if (remaining > 0) {
    groupLabel.push(
      ...[
        '; ',
        formatCurrencyAmount({
          currency,
          value: remaining,
        }),
        ' remaining',
      ]
    )
  }

  return (
    <StrictMode>
      <div
        aria-describedby={describedBy.join(' ')}
        aria-label={groupLabel.join('')}
        css={[styles.container({ theme }), styleOverrides.panel]}
        role="group"
        tabIndex={-1}
      >
        <BudgetHeader
          ariaHidden
          showInfoTooltip={true}
          styleOverrides={[styles.header, styleOverrides.header].flat()}
          title={headerTitle}
          {...{
            context,
            currency,
            max,
            theme,
          }}
        />

        <div css={[styles.allocation({ theme }), styleOverrides.allocation]}>
          <Conditional condition={context === StoryfirstUnit.Story}>
            <BudgetAllocation
              budgetType={StoryfirstUnit.Moment}
              htmlId={momentDescriptionId.current}
              styleOverrides={styles.momentAllocation}
              value={momentValue}
              {...{
                context,
                currency,
                max,
                theme,
              }}
            />
          </Conditional>

          <BudgetAllocation
            budgetType={StoryfirstUnit.Content}
            htmlId={contentDescriptionId.current}
            max={contentMax}
            value={contentValue}
            {...{
              context,
              currency,
              theme,
            }}
          />
        </div>
      </div>
    </StrictMode>
  )
}
BudgetPanel.displayName = 'Photon: BudgetPanel'
